import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { allComics, categories } from '@/mocks/comics';
import SchemaInjector from '@/components/base/SchemaInjector';
import { getWebPageSchema, getBreadcrumbSchema, getArticleSchema } from '@/mocks/schema';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = allComics.find((c) => c.slug === slug) || allComics[0];
  const [showAllChapters, setShowAllChapters] = useState(false);

  const relatedArticles = useMemo(() => {
    const sameCategory = allComics.filter((c) => c.id !== article.id && c.categorySlug === article.categorySlug);
    const others = allComics.filter((c) => c.id !== article.id && c.categorySlug !== article.categorySlug);
    return [...sameCategory.slice(0, 5), ...others.slice(0, 1)].slice(0, 6);
  }, [article.id, article.categorySlug]);

  const allChapters = useMemo(
    () =>
      Array.from({ length: Math.min(article.chapters, 100) }, (_, i) => ({
        num: article.chapters - i,
        title: i === 0 ? `第${article.chapters - i}话 - 最新` : `第${article.chapters - i}话`,
        date: `2026-${String(7 - Math.floor(i / 30)).padStart(2, '0')}-${String((i % 28) + 1).padStart(2, '0')}`,
      })),
    [article.chapters]
  );

  const displayChapters = showAllChapters ? allChapters : allChapters.slice(0, 20);

  const categoryInfo = categories.find((c) => c.slug === article.categorySlug);

  const schemas = [
    getWebPageSchema({
      title: `${article.title} - 韩漫之家 | 韩国漫画免费阅读`,
      description: article.description,
      url: `http://informyi.com/article/${slug}`,
    }),
    getBreadcrumbSchema([
      { name: '首页', url: 'http://informyi.com/' },
      { name: article.category, url: `http://informyi.com/category/${article.categorySlug}` },
      { name: article.title, url: `http://informyi.com/article/${slug}` },
    ]),
    getArticleSchema({
      title: article.title,
      description: article.description,
      url: `http://informyi.com/article/${slug}`,
      imageUrl: article.imageUrl,
      author: article.author,
    }),
  ];

  return (
    <>
      <SchemaInjector schema={schemas} />
      <Navbar />
      <main>
        <section className="relative w-full h-[350px] md:h-[480px] overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/45 to-black/35"></div>
          <div className="absolute inset-0 z-10 flex items-center">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-7xl mx-auto">
                <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
                  <div className="lg:w-2/5 text-center lg:text-left">
                    <div className="flex items-center gap-2 justify-center lg:justify-start mb-3 flex-wrap">
                      <Link
                        to={`/category/${article.categorySlug}`}
                        className="inline-block bg-accent-500 text-background-50 text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
                      >
                        {article.category}
                      </Link>
                      {article.isHot && (
                        <span className="inline-block bg-primary-500 text-background-50 text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                          HOT
                        </span>
                      )}
                    </div>
                    <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 mb-3 leading-tight">
                      {article.title}
                    </h1>
                    <p className="text-base text-background-50/80 leading-relaxed max-w-lg mx-auto lg:mx-0 mb-5">
                      {article.description}
                    </p>
                    <div className="flex flex-wrap items-center gap-3 justify-center lg:justify-start text-sm text-background-50/70 mb-6">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line"></i> {article.author}
                      </span>
                      <span className="w-1 h-1 rounded-full bg-background-50/40"></span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i> {article.views} 阅读
                      </span>
                      <span className="w-1 h-1 rounded-full bg-background-50/40"></span>
                      <span className="flex items-center gap-1">
                        <i className="ri-star-fill text-accent-500"></i> {article.rating}
                      </span>
                      <span className="w-1 h-1 rounded-full bg-background-50/40"></span>
                      <span>共{article.chapters}话</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 justify-center lg:justify-start">
                      {article.tags.map((tag) => (
                        <span
                          key={tag}
                          className="text-xs text-background-50/90 bg-background-50/15 px-3 py-1 rounded-full whitespace-nowrap"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="hidden lg:block lg:w-3/5"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <nav className="bg-background-50 border-b border-background-200/70">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto py-3">
              <ol className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
                <li><Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li><Link to={`/category/${article.categorySlug}`} className="hover:text-primary-500 transition-colors whitespace-nowrap">{article.category}</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-foreground-950 font-medium">{article.title}</li>
              </ol>
            </div>
          </div>
        </nav>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-10">
                <div className="lg:w-2/3">
                  <article className="bg-background-50 rounded-2xl p-6 md:p-8">
                    <div className="flex items-center gap-3 mb-6 pb-6 border-b border-background-200/70">
                      <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center">
                        <i className="ri-book-open-line text-accent-700 text-lg"></i>
                      </div>
                      <div>
                        <h2 className="text-lg font-bold text-foreground-950">作品详情</h2>
                        <p className="text-xs text-foreground-500">作者：{article.author} | 评分：{article.rating}/5</p>
                      </div>
                    </div>

                    <h3 className="text-lg font-bold text-foreground-950 mb-3">作品简介</h3>
                    <p className="text-foreground-700 leading-relaxed mb-6 text-sm">
                      {article.description}{' '}
                      这部作品由{article.author}创作，以其精彩的剧情和精美的画风深受广大读者喜爱。
                      目前已更新至第{article.chapters}话，累计阅读量突破{article.views}。
                    </p>

                    <div className="flex items-center gap-3 p-4 bg-background-100 rounded-xl mb-8">
                      <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center shrink-0">
                        <i className="ri-play-fill text-background-50 text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-950">从第一章开始阅读</p>
                        <p className="text-xs text-foreground-500">已更新 {article.chapters} 话</p>
                      </div>
                      <Link
                        to="#"
                        className="ml-auto bg-primary-500 text-background-50 px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                      >
                        开始阅读
                      </Link>
                    </div>

                    <h3 className="text-lg font-bold text-foreground-950 mb-4">
                      章节列表
                      <span className="text-xs font-normal text-foreground-500 ml-2">共{article.chapters}话</span>
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                      {displayChapters.map((ch) => (
                        <a
                          key={ch.num}
                          href="#"
                          className="flex items-center justify-between bg-background-100 hover:bg-background-200/70 rounded-lg px-4 py-3 transition-colors cursor-pointer group"
                        >
                          <span className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors">
                            {ch.title}
                          </span>
                          <span className="text-xs text-foreground-500 shrink-0">{ch.date}</span>
                        </a>
                      ))}
                    </div>
                    {allChapters.length > 20 && (
                      <button
                        onClick={() => setShowAllChapters(!showAllChapters)}
                        className="w-full text-center text-sm text-foreground-600 hover:text-primary-500 py-3 transition-colors cursor-pointer"
                      >
                        {showAllChapters ? '收起章节列表' : `查看全部 ${allChapters.length} 话`}
                        {showAllChapters ? <i className="ri-arrow-up-s-line ml-1"></i> : <i className="ri-arrow-down-s-line ml-1"></i>}
                      </button>
                    )}
                  </article>

                  {categoryInfo && (
                    <div className="mt-6 bg-background-50 rounded-2xl p-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                          <i className={`${categoryInfo.icon} text-lg`}></i>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-foreground-950">
                            更多{categoryInfo.name}类漫画
                          </p>
                          <p className="text-xs text-foreground-500">
                            共{categoryInfo.count}部作品
                          </p>
                        </div>
                        <Link
                          to={`/category/${article.categorySlug}`}
                          className="ml-auto text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                        >
                          查看全部
                          <i className="ri-arrow-right-line ml-1"></i>
                        </Link>
                      </div>
                    </div>
                  )}
                </div>

                <div className="lg:w-1/3">
                  <div className="bg-background-50 rounded-2xl p-6 sticky top-20">
                    <div className="aspect-[3/4] rounded-xl overflow-hidden mb-5">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <h3 className="text-lg font-bold text-foreground-950 mb-3">{article.title}</h3>
                    <div className="space-y-2.5 text-sm text-foreground-600 mb-5">
                      <div className="flex items-center justify-between">
                        <span>作者</span>
                        <span className="text-foreground-950 font-medium">{article.author}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>分类</span>
                        <Link to={`/category/${article.categorySlug}`} className="text-primary-500 font-medium hover:text-primary-600 transition-colors cursor-pointer">{article.category}</Link>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>章节</span>
                        <span className="text-foreground-950 font-medium">{article.chapters}话</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>评分</span>
                        <span className="flex items-center gap-1 text-accent-500 font-medium">
                          <i className="ri-star-fill"></i> {article.rating}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>阅读量</span>
                        <span className="text-foreground-950 font-medium">{article.views}</span>
                      </div>
                    </div>
                    <Link
                      to="#"
                      className="block text-center bg-accent-500 text-background-50 py-3.5 rounded-full font-semibold text-sm whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer mb-3"
                    >
                      开始阅读第一章
                    </Link>
                    <Link
                      to={`/category/${article.categorySlug}`}
                      className="block text-center bg-background-100 text-foreground-700 py-3 rounded-full font-medium text-sm whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer"
                    >
                      更多{article.category}漫画
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-10">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-widest text-foreground-400 mb-2">推荐阅读</p>
                  <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
                    猜你喜欢
                  </h2>
                </div>
                <p className="text-foreground-600 text-sm max-w-lg leading-relaxed">
                  根据您的阅读喜好，为您推荐以下精彩韩漫作品
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                {relatedArticles.map((comic) => (
                  <div key={comic.id} className="group">
                    <Link to={`/article/${comic.slug}`} className="block">
                      <div className="relative rounded-xl overflow-hidden mb-3" style={{ aspectRatio: '3/4' }}>
                        <img
                          src={comic.imageUrl}
                          alt={comic.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                          <div className="flex items-center gap-1 text-xs text-background-50/80">
                            <i className="ri-star-fill text-accent-500"></i>
                            <span>{comic.rating}</span>
                          </div>
                        </div>
                      </div>
                      <h3 className="text-sm font-bold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2 mb-1">
                        {comic.title}
                      </h3>
                      <p className="text-xs text-foreground-500">{comic.author}</p>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}